/* Tables driving the canvasOps engine, derived from the vendored canvasguard-schema.json
   (single source: FETIAS.Business/docs — see README.md). Three surfaces:

   1. UNIT_CATALOG — the proposal §4 unit table (25 units): where each unit lives in the
      address grammar (parent unit + collection segment), its identity key, and its verbs.
   2. Tier resolution — x-ai-write per key, resolved against the schema definitions
      (explicit annotation wins; unannotated keys in an open section default to "open").
   3. REFERENCE_GRAPH — x-reference-graph, the reverse-dependency edges the P5 sweep walks.

   Everything here is lookup, no canvas logic. */

import schema from "./canvasguard-schema.json";

export const VERBS = { upsert: "upsert", remove: "remove", move: "move", set: "set" };

/* Automation block graphs (triggers[].automation / automationBefore, and the form-graph
   twin on activity designs) are excluded op territory in v1 (proposal §4, D7): their node
   configs embed plugin/logic scripts the engine cannot statically tier. The engine treats
   these keys like code: carried over verbatim on replace, refused when a payload changes
   one. */
export const GRAPH_KEYS = ["automation", "automationBefore", "blocks"];

/* ---- unit catalog -------------------------------------------------------------------- */
/* children: collection segment (may be a dotted path off the node) -> child unit name.
   idKey "index" compares on the row's index VALUE (GUID string in newer canvases, a number
   in older ones) — always string-coerced. Verbs are the §4 table verbatim. */

const DESIGN_CHILDREN = { "design.rows": "formRow", "design.aiActions": "aiAction" };

export const UNIT_CATALOG = {
  root: {
    children: {
      information: "informationField",
      states: "state",
      flows: "flow",
      activities: "activity",
      standardActivities: "standardActivity",
      menus: "menu",
      documents: "document",
      triggers: "trigger",
      actions: "action",
    },
    /* §4 units 1–4: the only keys settable on the canvas root. */
    rootScalarKeys: ["ai_instruction", "title", "transaction", "archive"],
  },

  informationField: {
    def: ["InformationField"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id", "fieldName"],
    children: { fields: "subField", optionList: "option" },
    sweep: "informationField",
  },
  subField: {
    def: ["SubField"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id", "fieldName"],
    children: { optionList: "option" },
    sweep: "subField",
  },
  option: {
    def: ["OptionItem"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
    sweep: "option",
  },
  state: {
    def: ["State"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
    sweep: "state",
  },
  flow: {
    def: ["Flow"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 }, // no move — the transition set is unordered
    identityKeys: ["id"],
    children: { conditionalStates: "conditionalRoute" },
  },
  conditionalRoute: {
    def: ["Flow", "properties", "conditionalStates", "items"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 }, // move = priority (first match wins)
    identityKeys: ["id"],
  },
  activity: {
    def: ["Activity"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
    children: DESIGN_CHILDREN,
    sweep: "activity",
  },
  standardActivity: {
    def: ["StandardActivity"],
    idKey: "id",
    verbs: { upsert: 1, set: 1 }, // fixed set: no remove, no move
    identityKeys: ["id"],
    children: DESIGN_CHILDREN,
  },
  formRow: {
    def: ["DesignRow"],
    idKey: "index",
    verbs: { upsert: 1, remove: 1, move: 1 }, // no set — a row is structure only
    identityKeys: ["index"],
    children: { items: "formItem" },
  },
  formItem: {
    def: ["DesignItem"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
    children: {
      fields: "itemSubfieldConfig",
      tabs: "formTab",
      ...DESIGN_CHILDREN, // group/section items nest a design
    },
  },
  itemSubfieldConfig: {
    def: ["DesignItem", "properties", "fields", "items"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
  },
  formTab: {
    def: ["DesignItem", "properties", "tabs", "items"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
    children: DESIGN_CHILDREN,
  },
  aiAction: {
    def: ["AiAction"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
  },
  menu: {
    def: ["Menu"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
    children: {
      "list.columns": "listColumn",
      "mobileListDesign.columns": "mobileColumn",
      "filter.items": "filterItem",
      "defaultFilter.items": "filterItem",
      "defaultAppFilter.items": "filterItem",
    },
    sweep: "menu",
  },
  listColumn: {
    def: ["MenuColumn"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
  },
  mobileColumn: {
    def: ["MenuColumn"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, move: 1, set: 1 },
    identityKeys: ["id"],
  },
  filterItem: {
    def: ["FilterGroup", "properties", "items", "items"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
    children: { items: "filterItem" }, // group nodes (type:'or') nest conditions
  },
  document: {
    def: ["Document"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
    /* §4 unit 23: metadata only — template BODIES stay the htmljs bar's lane. */
    setWhitelist: ["name", "prefix", "dataSource", "type"],
    payloadDenied: ["html", "template", "templates", "rdlc"],
    children: { "userInput.rows": "formRow", "userInput.aiActions": "aiAction" },
    sweep: "document",
  },
  trigger: {
    def: ["Trigger"],
    idKey: "id",
    /* D19: upsert added — the propose_automation lane emits whole-trigger upserts; the
       graph itself is ONE unit of alteration, validated by validateGraphPayload (never
       partially edited, scripts inside block configs never authorable). */
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
    /* legacy script bodies stay closed on this lane (the D17 editor is their surface) */
    payloadDenied: ["beforeScript", "afterScript"],
    setWhitelist: [
      "name", "enabled", "actionType",
      "repeat", "recurringType", "every", "onceOn", "targetTime",
      "stateId", "activityId",
      /* legacy key, migration-flagged; settable so a document-remove cascade can CLEAR it
         (the referrer edge triggers[].documentId is blocking and must be cascadable) */
      "documentId",
      /* D19: whole-graph replacement (validated) */
      "automation", "automationBefore",
    ],
  },
  action: {
    def: ["Action"],
    idKey: "id",
    verbs: { upsert: 1, remove: 1, set: 1 },
    identityKeys: ["id"],
    sweep: "action",
  },
};

/* ---- schema definition + tier lookup ------------------------------------------------- */

const resolveRef = (node) => {
  if (node && node.$ref) {
    const name = node.$ref.replace("#/definitions/", "");
    return schema.definitions[name] || null;
  }
  return node;
};

/* Follow a UNIT_CATALOG def path into the schema definitions. */
export const definitionFor = (unitName) => {
  const unit = UNIT_CATALOG[unitName];
  if (!unit || !unit.def) return null;
  let node = schema.definitions[unit.def[0]] || null;
  for (let i = 1; node && i < unit.def.length; i++) {
    node = resolveRef(node[unit.def[i]]);
  }
  return resolveRef(node);
};

export const propertySchema = (unitName, key) => {
  const def = definitionFor(unitName);
  if (!def || !def.properties) return null;
  return def.properties[key] ? resolveRef(def.properties[key]) : null;
};

/* Write-policy tier of one key on one unit:
   "identity" | "graph" | explicit x-ai-write | "open" (known key, no annotation)
   | "unknown" (key the schema does not describe — canvas is open; warns, never refuses). */
export const tierOfKey = (unitName, key) => {
  const unit = UNIT_CATALOG[unitName];
  if (unit && unit.identityKeys && unit.identityKeys.indexOf(key) >= 0) return "identity";
  if (GRAPH_KEYS.indexOf(key) >= 0) return "graph";
  const def = definitionFor(unitName);
  if (!def || !def.properties) return "unknown";
  const prop = def.properties[key];
  if (!prop) return "unknown";
  if (prop["x-ai-write"]) return prop["x-ai-write"];
  return "open";
};

/* Root-level sections carry their tier on the schema's top-level properties. */
export const tierOfSection = (sectionKey) => {
  const prop = schema.properties[sectionKey];
  if (!prop) return "unknown";
  return prop["x-ai-write"] || "open";
};

export const FIELD_TYPES = schema.definitions.FieldType.enum.slice();
export const FIELD_TYPE_NAMES = schema.definitions.FieldType["x-enum-names"];

export const REFERENCE_GRAPH = schema["x-reference-graph"];

export const getSchema = () => schema;
